/**
 * The Color filter lists every raw product color value (e.g. "Kawaii",
 * "Galaxia", "New Mosto") as a flat, unscannable list. Each swatch's actual
 * color already comes from a real CSS rule (assets/theme.css
 * `.color-option.<handleized-value>`), so instead of asking merchants to
 * maintain a second color-name mapping, this reads each swatch's computed
 * color/pattern in the browser and buckets it into a small set of familiar
 * groups (Blues, Greens, Prints & Patterns, ...).
 *
 * Values already grouped by the merchant via Shopify's Search & Discovery
 * "Filter Setting Groups" (e.g. "Blue Family") carry a data-family-hint
 * attribute (see snippets/collection-filter.liquid) and are placed directly
 * into that named bucket instead of being color-computed. Since those
 * swatches have no real color of their own (their value is an opaque id),
 * they're given a representative solid color per bucket via
 * BUCKET_SWATCH_COLOR instead of the CSS fallback's rainbow gradient.
 *
 * A bucket containing only one swatch renders that swatch directly,
 * skipping the collapsible group header - there's nothing to disclose.
 */
(function () {
  const BUCKET_ORDER = [
    'Black',
    'White',
    'Grey',
    'Red',
    'Pink',
    'Orange',
    'Yellow',
    'Brown',
    'Green',
    'Blue',
    'Purple',
    'Prints & Patterns',
    'More Colors',
  ];

  // Representative swatch color for merchant "Filter Setting Group" values
  // (e.g. "Blue Family"), which have no single real color of their own.
  const BUCKET_SWATCH_COLOR = {
    Black: '#111111',
    White: '#ffffff',
    Grey: '#adadad',
    Red: '#d62828',
    Pink: '#e88bab',
    Orange: '#f4a300',
    Yellow: '#f2d602',
    Brown: '#7b4b2a',
    Green: '#2a9d63',
    Blue: '#3a6ea5',
    Purple: '#6a4c93',
  };

  function parseRgb(colorString) {
    const match = colorString && colorString.match(/rgba?\(([^)]+)\)/);
    if (!match) return null;
    const parts = match[1].split(',').map((part) => parseFloat(part.trim()));
    const [r, g, b, a = 1] = parts;
    if (a === 0 || Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return null;
    return { r, g, b };
  }

  function rgbToHsl({ r, g, b }) {
    const rN = r / 255;
    const gN = g / 255;
    const bN = b / 255;
    const max = Math.max(rN, gN, bN);
    const min = Math.min(rN, gN, bN);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case rN:
          h = (gN - bN) / d + (gN < bN ? 6 : 0);
          break;
        case gN:
          h = (bN - rN) / d + 2;
          break;
        default:
          h = (rN - gN) / d + 4;
      }
      h *= 60;
    }

    return { h, s: s * 100, l: l * 100 };
  }

  function bucketFromHsl({ h, s, l }) {
    if (s <= 10) {
      if (l >= 90) return 'White';
      if (l <= 12) return 'Black';
      return 'Grey';
    }
    if (l >= 93) return 'White';
    if (l <= 10) return 'Black';
    if (h >= 340 || h < 10) return l >= 65 ? 'Pink' : 'Red';
    if (h < 40) return l < 40 ? 'Brown' : 'Orange';
    if (h < 65) return 'Yellow';
    if (h < 170) return 'Green';
    if (h < 260) return 'Blue';
    if (h < 300) return 'Purple';
    return l >= 55 ? 'Pink' : 'Purple';
  }

  function bucketForSwatch(swatchEl) {
    const style = getComputedStyle(swatchEl);
    if (style.backgroundImage && style.backgroundImage !== 'none') {
      return 'Prints & Patterns';
    }
    const rgb = parseRgb(style.backgroundColor);
    if (!rgb) return 'More Colors';
    return bucketFromHsl(rgbToHsl(rgb));
  }

  function buildGroupElement(name, labels) {
    const group = document.createElement('div');
    group.className = 'collection-filter__color-group';

    const hasActive = labels.some((label) => label.querySelector('input:checked'));

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'collection-filter__color-group-toggle';
    toggle.setAttribute('aria-expanded', String(hasActive));
    toggle.innerHTML =
      '<span>' + name + ' <span class="collection-filter__count">(' + labels.length + ')</span></span>' +
      '<span class="collection-filter__color-group-chevron" aria-hidden="true">+</span>';

    const content = document.createElement('div');
    content.className = 'collection-filter__color-group-content';
    if (!hasActive) content.hidden = true;
    labels.forEach((label) => content.appendChild(label));

    toggle.addEventListener('click', () => {
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!expanded));
      content.hidden = expanded;
    });

    group.appendChild(toggle);
    group.appendChild(content);
    return group;
  }

  function groupColorSwatches() {
    const list = document.querySelector('[data-color-swatch-list]');
    if (!list) return;

    const labels = Array.from(list.querySelectorAll(':scope > label.collection-filter__color-option'));
    if (labels.length === 0) return;

    const buckets = new Map();
    labels.forEach((label) => {
      // Merchant-curated Shopify "Filter Setting Group" values (e.g. "Blue
      // Family") carry an opaque id instead of a real color, so trust the
      // merchant's own family name rather than guessing from a swatch color.
      const familyHint = label.getAttribute('data-family-hint');
      let bucket;
      if (familyHint) {
        bucket = familyHint;
        const swatch = label.querySelector('.variant-option.color-option');
        const representativeColor = BUCKET_SWATCH_COLOR[familyHint];
        if (swatch && representativeColor) {
          swatch.style.backgroundImage = 'none';
          swatch.style.backgroundColor = representativeColor;
        }
      } else {
        const swatch = label.querySelector('.variant-option.color-option');
        bucket = swatch ? bucketForSwatch(swatch) : 'More Colors';
      }
      if (!buckets.has(bucket)) buckets.set(bucket, []);
      buckets.get(bucket).push(label);
    });

    list.innerHTML = '';
    list.removeAttribute('data-color-swatch-list');
    list.classList.add('collection-filter__color-groups');

    const orderedNames = BUCKET_ORDER.filter((name) => buckets.has(name));
    const extraNames = Array.from(buckets.keys()).filter((name) => !BUCKET_ORDER.includes(name));
    orderedNames.concat(extraNames).forEach((name) => {
      const bucketLabels = buckets.get(name);
      // A bucket with only one swatch gains nothing from a collapsible
      // group - render the swatch directly instead of behind a toggle.
      if (bucketLabels.length === 1) {
        const wrapper = document.createElement('div');
        wrapper.className = 'collection-filter__color-group collection-filter__color-group--single';
        wrapper.appendChild(bucketLabels[0]);
        list.appendChild(wrapper);
      } else {
        list.appendChild(buildGroupElement(name, bucketLabels));
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', groupColorSwatches);
  } else {
    groupColorSwatches();
  }
})();
